import type { ThirdPartyTool } from '../../utils/detector-utils'

type ToolVendorDetails = {
  name: string
  /** The tool whose logo or icon represents the vendor. */
  tool: ThirdPartyTool
  /** `icon` when the vendor's only mark is an icon, so its name has to be written beside it. */
  mark: 'logo' | 'icon'
}

/**
 * The companies behind the third-party tools, for lists that name each vendor once. A vendor can
 * group tools that `ToolLogo` draws with different logos: Checkmarx and Checkmarx Enterprise are
 * one vendor, as are GitLab SAST and GitLab dependency scanning.
 */
export const TOOL_VENDORS = {
  aikido: { name: 'Aikido', tool: 'aikido_sast', mark: 'logo' },
  appscan: { name: 'AppScan', tool: 'appscan', mark: 'logo' },
  arnica: { name: 'Arnica', tool: 'arnica_sast', mark: 'logo' },
  black_duck: { name: 'Black Duck', tool: 'polaris_sast', mark: 'logo' },
  checkmarx: { name: 'Checkmarx', tool: 'checkmarx', mark: 'logo' },
  codeql: { name: 'CodeQL', tool: 'codeql', mark: 'icon' },
  contrast: { name: 'Contrast', tool: 'contrast', mark: 'logo' },
  datadog: { name: 'Datadog', tool: 'datadog_sast', mark: 'logo' },
  dependabot: { name: 'Dependabot', tool: 'dependabot', mark: 'logo' },
  fortify: { name: 'Fortify', tool: 'fortify', mark: 'logo' },
  gitlab: { name: 'GitLab', tool: 'gitlab_sast', mark: 'logo' },
  gitleaks: { name: 'Gitleaks', tool: 'gitleaks', mark: 'icon' },
  semgrep: { name: 'Semgrep', tool: 'semgrep', mark: 'logo' },
  snyk: { name: 'Snyk', tool: 'snyk', mark: 'logo' },
  sonar: { name: 'SonarQube', tool: 'sonar', mark: 'logo' },
  veracode: { name: 'Veracode', tool: 'veracode', mark: 'logo' },
  zap: { name: 'ZAP', tool: 'zap', mark: 'logo' },
} as const satisfies Record<string, ToolVendorDetails>

export type ToolVendor = keyof typeof TOOL_VENDORS

/** `satisfies Record` makes a new third-party `Tool` a type error here until it is given a vendor. */
export const VENDOR_BY_TOOL = {
  aikido_sast: 'aikido',
  aikido_sca: 'aikido',
  appscan: 'appscan',
  arnica_sast: 'arnica',
  black_duck_hub_sca: 'black_duck',
  checkmarx: 'checkmarx',
  checkmarx_enterprise: 'checkmarx',
  codeql: 'codeql',
  contrast: 'contrast',
  datadog_sast: 'datadog',
  dependabot: 'dependabot',
  fortify: 'fortify',
  gitlab_dependency_scanning: 'gitlab',
  gitlab_sast: 'gitlab',
  gitleaks: 'gitleaks',
  polaris_sast: 'black_duck',
  polaris_sca: 'black_duck',
  semgrep: 'semgrep',
  snyk: 'snyk',
  sonar: 'sonar',
  veracode: 'veracode',
  zap: 'zap',
} as const satisfies Record<ThirdPartyTool, ToolVendor>

/** Every vendor with at least one supported tool, ordered alphabetically by name. */
export const SUPPORTED_TOOL_VENDORS: readonly ToolVendor[] = [...new Set(Object.values(VENDOR_BY_TOOL))].sort((a, b) =>
  TOOL_VENDORS[a].name.localeCompare(TOOL_VENDORS[b].name)
)
